import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { CallDirection } from 'common/Enums'
import IncomingCallPopup from './IncomingCallPopup'
import { ActiveCallRingtone, RingtoneConfig } from 'store/models/Ringtones'
import { visualNumberFormat } from 'utils/numberUtils'
import { useTranslation } from 'react-i18next'
import { sendNotification } from 'common/Helpers'
import MinimizedCalls from './MinimizedCalls'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { usePrevious } from 'hooks'
import { isEqual } from 'lodash'
import { CallerInsightsPopup } from 'components/shared'

export default function IncomingCalls() {
    const { t } = useTranslation()

    const { selectedConfigIds, volume, autoPickup } = useSelector((state) => state.settings)
    const { integration } = useSelector((state) => state.profile)
    const { calls } = useSelector((state) => state.call)

    const prevPhoneNumbers = usePrevious(calls) || []

    const [incomingCalls, setIncomingCalls] = useState([])
    const [activeCalls, setActiveCalls] = useState([])
    const [minimizedCalls, setMinimizedCalls] = useState([])
    const [click2CallCalls, setClick2CallCalls] = useState([])
    const [finishedCalls, setFinishedCalls] = useState([])

    const [ringtoneAudio, setRingtoneAudio] = useState({})
    const [activeCallRingtoneAudio, setActiveCallRingtoneAudio] = useState({})
    const [ringtone, setRingtone] = useState({})

    const selectedRingtone = RingtoneConfig.find((item) => item.id === selectedConfigIds.ringtoneId)?.src
    const activeCallRingtone = ActiveCallRingtone.src

    const setAudioDevice = (audio) => {
        if (typeof audio.setSinkId === 'function') {
            audio
                .setSinkId(selectedConfigIds.speakerRingtoneDeviceId || '')
                .then()
                .catch((error) => console.error(error))
        }
    }

    const prepareRingtoneAudio = (ringtoneSrc) => {
        const audio = new Audio(ringtoneSrc)

        audio.volume = 0.01 * volume
        audio.preload = true
        audio.loop = true

        setAudioDevice(audio)
        return audio
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }

    const filterIncomingCall = (item) => item.direction === CallDirection.Incoming

    useEffect(() => {
        setRingtoneAudio(prepareRingtoneAudio(selectedRingtone))
        setActiveCallRingtoneAudio(prepareRingtoneAudio(activeCallRingtone))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        setRingtone(ringtoneAudio)
    }, [ringtoneAudio])

    useEffect(() => {
        const incomingCalls = calls.filter(filterIncomingCall)
        const prevIncomingCalls = prevPhoneNumbers.filter(filterIncomingCall)
        const latestCall = incomingCalls[incomingCalls.length - 1]

        if (!isEqual(incomingCalls, prevIncomingCalls)) {
            setIncomingCalls(calls.filter((item) => item.isTrying && item.direction === CallDirection.Incoming))
        }

        if (incomingCalls.length > prevIncomingCalls.length) {
            sendNotification(t('notification.incoming_call'), visualNumberFormat(latestCall.number))
        }

        setActiveCalls(calls.filter((item) => item.isActive))
        setMinimizedCalls(calls.filter((item) => item.isMinimized))

        const finished = calls.filter((item) => item.isFinished)
        const prevFinished = prevPhoneNumbers.filter((item) => item.isFinished)
        finished.length && !prevFinished.length && setFinishedCalls(calls.filter((item) => item.isFinished))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calls])

    useEffect(() => {
        const hangupAudio = new Audio('/audio/call_hangup.mp3')
        hangupAudio.volume = 0.01 * volume

        if (finishedCalls.length) {
            hangupAudio.play()
        }
    }, [volume, finishedCalls])

    useEffect(() => {
        if (activeCalls.length) {
            setRingtone(activeCallRingtoneAudio)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCalls])

    useEffect(() => {
        setClick2CallCalls(incomingCalls.filter((item) => item.isClick2Call))
    }, [incomingCalls])

    useEffect(() => {
        if (incomingCalls.length && ringtone instanceof HTMLAudioElement) {
            ringtone.play()
            return () => ringtone.load()
        }
    }, [ringtone, incomingCalls.length])

    useEffect(() => {
        if (autoPickup && click2CallCalls.length) {
            click2CallCalls.map((item) => sendNotification(t('notification.call_to'), visualNumberFormat(item.number)))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoPickup, click2CallCalls])

    useEffect(() => {
        if (ringtone instanceof HTMLAudioElement) {
            ringtone.volume = 0.01 * volume
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volume])

    useEffect(() => {
        setAudioDevice(ringtone)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedConfigIds.speakerRingtoneDeviceId])

    const [insightsPopupShown, setInsightsPopupShown] = useState(false)

    const toggleInsightsPopup = (e) => {
        setInsightsPopupShown(!insightsPopupShown)
    }

    return (
        <>
            <div className="call__incoming-holder">
                <TransitionGroup className="call__incoming-holder--transitions">
                    {incomingCalls.map((item, i) => (
                        <CSSTransition timeout={200} key={i} classNames="call__incoming__transition">
                            <div className="call__incoming__details">
                                {item?.callerInsights && insightsPopupShown && (
                                    <CallerInsightsPopup
                                        number={item.number}
                                        insights={item.callerInsights}
                                        integration={integration}
                                        onClose={toggleInsightsPopup}
                                    />
                                )}
                                <IncomingCallPopup
                                    incomingCall={item}
                                    integration={integration}
                                    onShowLeadClick={toggleInsightsPopup}
                                />
                            </div>
                        </CSSTransition>
                    ))}
                </TransitionGroup>
                {minimizedCalls.length > 0 && (
                    <div className="call__minimized">
                        {minimizedCalls.map((item, i) => (
                            <MinimizedCalls key={i} call={item} minimizedCallsQty={minimizedCalls.length} />
                        ))}
                    </div>
                )}
            </div>
            {incomingCalls.length > 0 && !activeCalls.length && <div className="call__incoming-shadow" />}
        </>
    )
}
